import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, AirplaneTakeoff, Clock, Shield, Star, Car } from "@phosphor-icons/react";
import { Helmet } from "react-helmet-async";

const DestinationPage = ({ config }) => {
  const { slug, title, h1, subtitle, heroImage, intro, sections, prices, faq, schema } = config;

  return (
    <main className="pt-20 bg-[#EDEBE4]" data-testid={`destination-${slug}`}>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={intro.slice(0, 155)} />
        <link rel="canonical" href={`https://alturaconciergerie.com/${slug}`} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={intro.slice(0, 155)} />
        <meta property="og:url" content={`https://alturaconciergerie.com/${slug}`} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={heroImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="alternate" hrefLang="fr" href={`https://alturaconciergerie.com/${slug}`} />
        <link rel="alternate" hrefLang="en" href={`https://alturaconciergerie.com/${slug}`} />
        <link rel="alternate" hrefLang="x-default" href={`https://alturaconciergerie.com/${slug}`} />
        {schema && <script type="application/ld+json">{JSON.stringify(schema)}</script>}
      </Helmet>

      {/* Hero */}
      <section className="relative h-[50vh] min-h-[400px] overflow-hidden">
        <img src={heroImage} alt={h1} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#EDEBE4] via-[#EDEBE4]/60 to-transparent" />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-5xl mx-auto px-6 sm:px-12 pb-12 w-full">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
              <p className="text-xs tracking-[0.2em] uppercase text-[#1A1A1A]/70 mb-3">{subtitle}</p>
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-normal text-[#1A1A1A] leading-tight">{h1}</h1>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Intro + CTA */}
      <section className="py-16 sm:py-20">
        <div className="max-w-5xl mx-auto px-6 sm:px-12">
          <div className="grid lg:grid-cols-3 gap-12">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="lg:col-span-2">
              <p className="text-[#1A1A1A] font-light leading-relaxed text-base mb-8">{intro}</p>
              <div className="flex flex-wrap gap-4">
                <Link to="/services/deplacements" className="bg-[#1A1A1A] text-[#EDEBE4] px-8 py-3 text-sm tracking-wider uppercase hover:bg-[#1A1A1A]/80 transition-colors inline-flex items-center gap-2" data-testid="destination-cta-book">
                  Réserver maintenant <ArrowRight size={16} />
                </Link>
                <Link to="/contact" className="border border-[#1A1A1A] text-[#1A1A1A] px-8 py-3 text-sm tracking-wider uppercase hover:bg-[#1A1A1A] hover:text-[#EDEBE4] transition-colors" data-testid="destination-cta-contact">
                  Demander un devis
                </Link>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }} className="space-y-4">
              {prices.map((p, i) => (
                <div key={i} className="bg-white border border-[#1A1A1A]/10 p-4 flex justify-between items-center">
                  <div>
                    <p className="text-[#1A1A1A] text-sm font-medium">{p.vehicle}</p>
                    <p className="text-[#1A1A1A]/50 text-xs">{p.capacity}</p>
                  </div>
                  <p className="text-[#1A1A1A] font-heading text-lg">{p.price}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Content sections */}
      {sections.map((section, i) => (
        <section key={i} className={`py-16 ${i % 2 === 0 ? "bg-white" : "bg-[#EDEBE4]"}`}>
          <div className="max-w-5xl mx-auto px-6 sm:px-12">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <div className="flex items-center gap-3 mb-6">
                {section.icon}
                <h2 className="font-heading text-2xl sm:text-3xl font-normal text-[#1A1A1A]">{section.title}</h2>
              </div>
              <p className="text-[#1A1A1A]/80 font-light leading-relaxed max-w-3xl">{section.content}</p>
              {section.list && (
                <ul className="mt-6 space-y-3">
                  {section.list.map((item, j) => (
                    <li key={j} className="flex items-start gap-3 text-[#1A1A1A]/80 text-sm">
                      <span className="w-1.5 h-1.5 bg-[#1A1A1A] rounded-full mt-1.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </motion.div>
          </div>
        </section>
      ))}

      {/* FAQ */}
      {faq && faq.length > 0 && (
        <section className="py-16 sm:py-20 bg-[#EDEBE4]">
          <div className="max-w-5xl mx-auto px-6 sm:px-12">
            <h2 className="font-heading text-2xl sm:text-3xl font-normal text-[#1A1A1A] mb-10">Questions fréquentes</h2>
            <div className="space-y-6">
              {faq.map((item, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
                  <h3 className="text-[#1A1A1A] font-medium text-sm mb-2">{item.q}</h3>
                  <p className="text-[#1A1A1A]/70 text-sm font-light leading-relaxed">{item.a}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Bottom CTA */}
      <section className="py-16 bg-[#1A1A1A]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl text-[#EDEBE4] mb-4">Réservez votre transfert</h2>
          <p className="text-[#EDEBE4]/60 text-sm mb-8">Service disponible 24h/24, 7j/7. Confirmation immédiate.</p>
          <Link to="/services/deplacements" className="bg-[#EDEBE4] text-[#1A1A1A] px-10 py-4 text-sm tracking-wider uppercase hover:bg-white transition-colors inline-flex items-center gap-2" data-testid="destination-cta-bottom">
            Réserver <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </main>
  );
};

export default DestinationPage;
